import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { TixAnalyticsService } from '@tix/shared/analytics';
import { Store } from '@ngrx/store';
import { TixCompany } from '@tix/data-access';
import { LoggerService } from 'apps/purchasing-portal/src/app/logger.service';
import { UserSelector } from '@tix/auth/state';
import { take } from 'rxjs/operators';
import { TixCompanyPartialState } from '@tix/company/state';
import * as CompanyActions from '@tix/company/state/actions';
import * as CompanySelectors from '@tix/company/state/selectors';
import { UserActions, Roles } from '@tix/auth/state';
import {
  TixUserPreferencesService,
  USER_PREFERENCE_KEYS
} from '@tix/shared/state';
import { map, takeUntil } from 'rxjs/operators';
import { TixStaffService } from '@tix/staff/state';

@Component({
  selector: 'tix-company-list',
  templateUrl: './company-list.component.html',
  styleUrls: ['./company-list.component.scss']
})
export class TixCompanyListPage implements OnInit, OnDestroy {
  readonly companies$: Observable<TixCompany[]> = this.store.select(
    CompanySelectors.getFilteredCompanies
  );
  readonly loaded$: Observable<boolean> = this.store.select(
    CompanySelectors.getCompaniesLoaded
  );

  readonly hasDefaultCompany$ = this.userPreferencesService.preferences$.pipe(
    map(preferences => !!preferences[USER_PREFERENCE_KEYS.DEFAULT_COMPANY_ID])
  );

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<TixCompanyPartialState>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userPreferencesService: TixUserPreferencesService,
    private readonly staffService: TixStaffService,
    private logger: LoggerService,
    private analytics: TixAnalyticsService
  ) {}

  ngOnInit(): void {
    // Check if user wants to bypass default company redirect
    const bypassDefault = this.route.snapshot.queryParams['showAll'];

    // Subscribe to preferences and handle default company routing
    this.userPreferencesService.preferences$
      .pipe(takeUntil(this.destroy$))
      .subscribe(preferences => {
        const defaultCompanyId =
          preferences[USER_PREFERENCE_KEYS.DEFAULT_COMPANY_ID];

        if (defaultCompanyId && !bypassDefault) {
          // If default company is set and user didn't explicitly request to see all companies,
          // route to that company automatically based on user's role
          this.staffService
            .getCurrentUserRolesInCompany(defaultCompanyId)
            .pipe(take(1))
            .subscribe(roles => {
              let routePath: string[];

              if (roles.includes(Roles.CO_ADMIN)) {
                routePath = ['/company', defaultCompanyId, 'dashboard'];
              } else if (roles.includes(Roles.BO_USER)) {
                routePath = ['/company', defaultCompanyId, 'dashboard'];
              } else if (roles.includes(Roles.EVENT_ADMINISTRATOR)) {
                routePath = ['/company', defaultCompanyId, 'events'];
              } else {
                routePath = ['/company', defaultCompanyId, 'reports'];
              }

              this.router.navigate(routePath);
            });
          return;
        }

        // If no default company is set or user wants to see all companies,
        // proceed with normal company list loading
        this.store.dispatch(UserActions.fetchUserRoles());

        setTimeout(() => {
          this.store.dispatch(CompanyActions.loadCompanies());

          this.store
            .select(UserSelector.getAuthenticatedUser)
            .pipe(take(1))
            .subscribe(user => {
              this.logger.info({
                message: 'Companies list viewed',
                event: 'company_list_view',
                data: {}
              });
            });
        }, 1000);
      });
  }

  handleInputChange(searchText: string): void {
    this.analytics.captureEvent('staff_search_used', {
      search_text: searchText
    });
    this.store.dispatch(CompanyActions.filterCompanies({ searchText }));

    // Log company search
    this.store
      .select(UserSelector.getAuthenticatedUser)
      .pipe(take(1))
      .subscribe(user => {
        this.logger.info({
          message: 'Companies list filtered',
          event: 'company_list_filter',
          data: { searchText }
        });
      });
  }

  onAddCompany(): void {
    this.analytics.captureEvent('staff_add_contact_clicked');
  }

  clearDefaultCompany(): void {
    this.userPreferencesService.removePreference(
      USER_PREFERENCE_KEYS.DEFAULT_COMPANY_ID
    );
  }

  showAllCompanies(): void {
    this.router.navigate(['/company'], { queryParams: { showAll: true } });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
